/* ============================================================================
   PURE SEA MOSS — carrito lateral
   Portado de CartSidebar.tsx (Astro) el 2026-09-14, sin framework.

   · Todo lo que añade al carrito es un <form action="/cart/add"> normal: sin
     JS funciona (va a /cart); con JS se intercepta, se añade por /cart/add.js
     y se abre el panel.
   · El HTML del panel lo pinta SIEMPRE Liquid (sections/cart-drawer.liquid),
     pedido con la Section Rendering API. Aquí no se construye ni una línea.
   · Trampa de foco real, Escape, `inert` al cerrar y el foco vuelve a quien
     abrió. En Astro el tabulador se escapaba por detrás del panel.
   ========================================================================== */

const SECTION_ID = 'cart-drawer';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.scrim = this.querySelector('[data-drawer-scrim]');
    this.returnTo = null;
    this.onKeyDown = this.onKeyDown.bind(this);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const button = event.target.closest('[data-qty-change]');
      if (button) {
        event.preventDefault();
        this.change(button.dataset.key, Number(button.dataset.quantity));
      }
    });

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (!toggle) return;
      event.preventDefault();
      this.open(toggle);
    });

    // La página /cart pinta las mismas líneas fuera del panel: allí cada
    // cambio de cantidad se guarda y se recarga la página entera.
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-page] [data-qty-change]');
      if (!button) return;
      event.preventDefault();
      button.closest('[data-cart-page]').setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: button.dataset.key, quantity: Number(button.dataset.quantity) }),
        });
        if (!response.ok) throw new Error(String(response.status));
        window.location.reload();
      } catch (err) {
        const page = button.closest('[data-cart-page]');
        page.removeAttribute('aria-busy');
        const error = page.querySelector('[data-cart-page-error]');
        if (error) {
          error.textContent = "We couldn't change that quantity. Please try again.";
          error.hidden = false;
        }
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[action$="/cart/add"]');
      if (!form || form.hasAttribute('data-no-drawer')) return;
      event.preventDefault();
      this.add(form, event.submitter);
    });
  }

  get isOpen() {
    return !this.panel.hasAttribute('inert');
  }

  open(opener) {
    if (this.isOpen) return;
    this.returnTo = opener || document.activeElement;
    this.panel.removeAttribute('inert');
    this.panel.classList.remove('drawer--closed');
    this.scrim.classList.remove('drawer-scrim--hidden');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeyDown);
    this.panel.querySelector('button, a[href]')?.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.panel.setAttribute('inert', '');
    this.panel.classList.add('drawer--closed');
    this.scrim.classList.add('drawer-scrim--hidden');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.onKeyDown);
    this.returnTo?.focus?.();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
      (el) => !el.hidden && el.offsetParent !== null,
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    // El ciclo se cierra a mano: sin esto el tabulador sale del panel hacia
    // una página oculta al ojo pero no al foco.
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async add(form, submitter) {
    const button = submitter || form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-add-error]');
    const label = button?.querySelector('[data-label]') || button;
    const idle = label?.textContent;
    if (error) error.hidden = true;
    if (button) button.disabled = true;
    if (label) label.textContent = 'Adding…';

    const body = new FormData(form);
    body.append('sections', SECTION_ID);
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      this.render(data.sections);
      this.open(button);
    } catch (err) {
      if (error) {
        error.textContent = "That didn't go through. Please try again.";
        error.hidden = false;
      }
    } finally {
      if (button) button.disabled = false;
      if (label && idle) label.textContent = idle;
    }
  }

  async change(key, quantity) {
    const content = this.querySelector('[data-drawer-content]');
    content?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: SECTION_ID,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      this.render(data.sections);
    } catch (err) {
      content?.removeAttribute('aria-busy');
      const error = this.querySelector('[data-drawer-error]');
      if (error) {
        error.textContent = "We couldn't change that quantity. Please try again.";
        error.hidden = false;
      }
    }
  }

  /** Sustituye el contenido del panel por el HTML que devolvió Shopify. */
  render(sections) {
    const html = sections?.[SECTION_ID];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-drawer-content]');
    const current = this.querySelector('[data-drawer-content]');
    if (!fresh || !current) return;
    current.replaceWith(fresh);
    this.updateCount(Number(fresh.dataset.count || 0));
    if (this.isOpen) this.panel.querySelector('button, a[href]')?.focus();
  }

  updateCount(count) {
    const label = `Cart, ${count} item${count === 1 ? '' : 's'}`;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
    document.querySelectorAll('[data-cart-toggle]').forEach((el) => {
      el.setAttribute('aria-label', label);
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
